import objectTypes from '../../constants/ObjectTypes';
import UIConstants from '../../constants/UIConstants';
import constants from '../../qti/Constants';
import marketplacePropertiesService from '../../services/MarketplacePropertiesService';
import uiService from '../../services/UIService';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import uiReader from '../../store/readHelpers/uiReader';
import themeServices from '../../themes/ThemeServices';

const displayTypeContainerSelector = {
    selectVerticalDisplay(section, currentQuestion) {
        if (section && !section.stimulusContent && currentQuestion && marketplacePropertiesService.isEnableStimulusInQuestion()) {
            const layout = currentQuestion.layout;
            if (layout) {
                return layout.includes('vertical');
            }
        }
        return !!section
        && assessmentReader.useVerticalDisplayForSection(section.id)
        && sessionReader.isViewMediaScreen();
    },

    selectShowQuestionRubricBlockOnTopLevel(showStimulus, isSplitQuestionView, currentQuestion) {
        const hasStimulusWithDynamicRubric = showStimulus && questionReader.hasStimulusDynamicRubricSupport(currentQuestion);

        return uiReader.showDynamicRubric()
            && !!currentQuestion.customFields
            && (isSplitQuestionView || hasStimulusWithDynamicRubric);
    },

    selectShowQuestionRubricBlockOnSectionLevel(showQuestionRubricOnTopLevel, currentQuestion) {
        if (!currentQuestion) {
            return false;
        }

        const { category, objectType, customFields } = currentQuestion;
        return uiReader.showDynamicRubric()
            && !showQuestionRubricOnTopLevel
            && !!customFields
            && category !== objectTypes.CATEGORY.STIMULUS
            && objectType !== constants.interactionType.GAP_MATCH_INTERACTION;
    },

    selectSectionNumber(section) {
        return section?.questions?.[0] ? section.questions[0].sectionNumber : null;
    },

    selectShowSectionRubricBlockOnTopLevel(showUpperLevelQuestionRubricBlock, shouldShowSectionRubric, isLeftSectionLayout) {
        // isLeftSectionLayout is true
        return !showUpperLevelQuestionRubricBlock && shouldShowSectionRubric && isLeftSectionLayout;
    },

    selectShowSectionRubricBlockOnSectionLevel(showUpperLevelQuestionRubricBlock, shouldShowSectionRubric, isLeftSectionLayout) {
        // isLeftSectionLayout is false
        return !showUpperLevelQuestionRubricBlock && shouldShowSectionRubric && !isLeftSectionLayout;
    },

    selectCurrentlyShownQuestionStimulus({ questionId }) {
        const currentlyShownQuestion = assessmentReader.getQuestion(questionId);
        return currentlyShownQuestion?.stimulus;
    },

    selectStimulusContent(showStimulus, section, questionStimulus) {
        if (!showStimulus) {
            return {
                holder: null,
                content: null,
            };
        }

        // section stimulus has higher priority for now - https://inspera.atlassian.net/browse/CDT-35?focusedCommentId=371492
        if (section.stimulusContent) {
            return {
                holder: UIConstants.STIMULUS_HOLDER.SECTION,
                content: section.stimulusContent,
            };
        }

        if (questionStimulus) {
            return {
                holder: UIConstants.STIMULUS_HOLDER.QUESTION,
                content: questionStimulus,
            };
        }

        return {
            holder: null,
            content: null,
        };
    },

    selectShowStimulus(section, questionStimulus) {
        const isCbStimulusSubmissionTabEnabled = marketplacePropertiesService.isEnableCbStimulusSubmissionTab();
        const isStimulusSupportedMode = section &&
            sessionReader.isViewMediaScreen() &&
            (
                (sessionReader.isModeSection() || sessionReader.isModeQuestionSet() || sessionReader.isModeQuestion()) &&
                !sessionReader.isAnswerKeysMode()
            );

        if (!isStimulusSupportedMode) {
            return false;
        }

        if (section.stimulusContent) {
            // section stimulus has higher priority. if it's provided then check if section layout supports stimulus
            return isCbStimulusSubmissionTabEnabled || uiService.isSectionLayoutWithStimulus(section);
        }

        return !!questionStimulus && (marketplacePropertiesService.isEnableStimulusInQuestion() || uiService.isBasicSectionLayout(section));
    },

    selectIsStimulusResizingAllowed(stimulusContent, { allowStimulusResizing }, question) {
        if (settingsReader.shouldHideResponseAreasForPaperSubmissions(question)
            || settingsReader.shouldHideResponseAreaInDemoTestForPaperSubmissions(question)) {
            return false;
        }
        if (!stimulusContent) {
            return false;
        }

        return allowStimulusResizing !== undefined ? allowStimulusResizing : marketplacePropertiesService.isStimulusResizingAllowed();
    },

    selectStimulusPosition(stimulusHolder, section) {
        if (marketplacePropertiesService.isEnableStimulusInQuestion()) {
            return assessmentReader.getSectionStimulusPosition(section);
        }
        return uiService.isQuestionStimulusHolder(stimulusHolder)
            ? UIConstants.STIMULUS_POSITION.LEFT
            : assessmentReader.getSectionStimulusPosition(section);
    },

    selectShouldAdjustSectionPaddings(isSplitQuestionView, section) {
        return isSplitQuestionView && uiService.isBasicSectionLayout(section);
    },

    selectShouldAddSectionAriaAttributes(showStimulus) {
        return showStimulus && uiService.areQuestionSectionsFocusable();
    },

    selectRegionAccessibilityAttributes(showStimulus) {
        return showStimulus ? uiService.getQuestionRegionsAccessibilityAttributes() : {};
    },

    selectProps(state) {
        const { currentlyShown, ui, response, settings, session, studentHighlights: { focusedAnnotation } } = state;
        const section = assessmentReader.getCurrentShownSection() || {};
        const question = assessmentReader.getCurrentQuestion() || {};

        const isLeftSectionLayout = uiService.isLeftSectionLayout(section);

        const questionStimulus = this.selectCurrentlyShownQuestionStimulus(currentlyShown);
        const showStimulus = this.selectShowStimulus(section, questionStimulus);
        const {
            content: stimulusContent,
            holder: stimulusHolder,
        } = this.selectStimulusContent(showStimulus, section, questionStimulus);

        const stimulusPosition = this.selectStimulusPosition(stimulusHolder, section);

        const currentQuestion = assessmentReader.getQuestion(currentlyShown.questionId);
        const isSplitQuestionView = questionReader.isSplitQuestionView(currentQuestion);

        const shouldShowSectionRubric = themeServices.getThemeInstance().shouldShowSectionRubric(section, ui);
        const showQuestionRubricBlockOnTopLevel = this.selectShowQuestionRubricBlockOnTopLevel(showStimulus, isSplitQuestionView, currentQuestion);
        const showQuestionRubricBlockOnSectionLevel =
            this.selectShowQuestionRubricBlockOnSectionLevel(showQuestionRubricBlockOnTopLevel, currentQuestion);
        const showUpperLevelQuestionRubricBlock = showQuestionRubricBlockOnTopLevel || showQuestionRubricBlockOnSectionLevel;
        const showSectionRubricBlockOnTopLevel = this.selectShowSectionRubricBlockOnTopLevel(showUpperLevelQuestionRubricBlock,
            shouldShowSectionRubric, isLeftSectionLayout);
        const showSectionRubricBlockOnSectionLevel = this.selectShowSectionRubricBlockOnSectionLevel(showUpperLevelQuestionRubricBlock,
            shouldShowSectionRubric, isLeftSectionLayout);

        const shouldAddSectionAriaAttributes = this.selectShouldAddSectionAriaAttributes(showStimulus);

        return {
            currentlyShown,
            currentQuestion,
            focusedAnnotation,
            isQuestionPreviewMode: sessionReader.isModeQuestion(),
            isSplitQuestionView,
            isStimulusResizingAllowed: this.selectIsStimulusResizingAllowed(stimulusContent, section, question),
            isTopStimulus: uiService.isTopStimulusPosition(stimulusPosition),
            regionAccessibilityAttributes: this.selectRegionAccessibilityAttributes(showStimulus),
            response,
            section,
            sectionNumber: this.selectSectionNumber(section),
            session,
            settings,
            shouldAddSectionAriaAttributes,
            shouldAdjustSectionPaddings: this.selectShouldAdjustSectionPaddings(isSplitQuestionView, section),
            shouldRenderAnswerKeys: sessionReader.isAnswerKeysMode(),
            showQuestionCopyrightText: uiReader.showQuestionCopyrightText(),
            showQuestionRubricBlockOnSectionLevel,
            showQuestionRubricBlockOnTopLevel,
            showSectionRubricBlockOnSectionLevel,
            showSectionRubricBlockOnTopLevel,
            showStimulus,
            showUpperLevelQuestionRubricBlock,
            stimulusContent,
            stimulusHolder,
            stimulusPosition,
            ui,
            verticalDisplay: this.selectVerticalDisplay(section, currentQuestion),
        };
    },
};

export default displayTypeContainerSelector;
